/**
 * Composer 동적 데이터셋 행 그리드의 등록형(field.widget:'registered') 도메인 위젯 레지스트리.
 * web 실폼·admin 캔버스(라이브 모드)가 같은 맵을 주입한다(rc 이동 2026-07-20 — WYSIWYG 패리티).
 *
 * DataSourceRowField.widgetKey로 이 맵에서 위젯을 찾아 주입한다.
 * 미등록 키는 RowsCollectionEditor가 readonly 표시 + 안내 title로 처리한다.
 *
 * 등록 위젯:
 *   'improvement-plan'  → ImprovementPlanWidget (부서/담당자/일정 값 객체 편집)
 *   'risk-estimation'   → RiskEstimationWidget (위험도 값 객체 readonly 표시)
 *   'improvement-image' → ImprovementImageWidget (개선 포인트(사례) AI 이미지 — 생성(유사 검색·재사용은
 *                         2026-09-14 제거 — widgets/improvementImageHost.ts 헤더 참조),
 *                         호스트 콜백은 context.improvementImage — 미배선이면 표시 전용)
 */
import { REGISTERED_OBJECT_FIELD_CONTRACTS } from './dataSourceWidgets';
import type { RegisteredRowWidget } from './RowsCollectionEditor';
import ImprovementImageWidget from './widgets/ImprovementImageWidget';
import ImprovementPlanWidget from './widgets/ImprovementPlanWidget';
import RiskEstimationWidget from './widgets/RiskEstimationWidget';
import { IMPROVEMENT_IMAGE_WIDGET_KEY } from './widgets/improvementImageHost';

export const COMPOSED_ROW_WIDGETS: Record<string, RegisteredRowWidget> = {
  'improvement-plan': ImprovementPlanWidget,
  'risk-estimation': RiskEstimationWidget,
  [IMPROVEMENT_IMAGE_WIDGET_KEY]: ImprovementImageWidget,
};

/**
 * widgetKey의 사람 라벨 — admin '위젯 키' select 표시용. **여기 없는 키도 목록에는 뜬다**
 * (키를 그대로 라벨로 씀) — 라벨 등재를 잊었다고 위젯이 목록에서 사라지면 저작자가 그 위젯을
 * 아예 고를 수 없게 되므로, 목록 자격은 아래 두 레지스트리가 정하고 여기는 표시만 맡는다.
 */
export const REGISTERED_ROW_WIDGET_LABELS: Record<string, string> = {
  'risk-estimation': '위험도 (빈도·강도 / 상중하)',
  'improvement-plan': '개선대책 (부서·담당자·일정)',
  // 동작 조건(인과): 작성 화면이 `onImprovementImage` 호스트 콜백을 주입해야 생성 버튼이 뜬다.
  // 미주입(admin 캔버스·외부 위임 무인증)이면 이미지 표시 전용 — admin select는 IMPROVEMENT_IMAGE_HOST_HINT로
  // 같은 인과를 저작자에게 보인다(widgets/improvementImageHost.ts). 유사 사례 재사용은 2026-09-14 제거.
  [IMPROVEMENT_IMAGE_WIDGET_KEY]: '개선 포인트(사례) 이미지 (AI 생성)',
};

/**
 * 유효한 widgetKey 후보 — 행 그리드 구현(COMPOSED_ROW_WIDGETS)과 중첩 자식 계약
 * (REGISTERED_OBJECT_FIELD_CONTRACTS)의 **합집합**. 둘 중 하나만 있어도 그 키는 의미가 있다:
 * 구현만 있으면 작성 화면이 전용 위젯을 주입하고, 계약만 있으면 저작자가 자식 경로
 * (`<필드>.<자식>`)를 행 필드·표시 키 후보에서 고를 수 있다.
 *
 * 목록을 **레지스트리에서 파생**하는 것이 요점이다 — 새 특화 위젯을 등재하면 admin select에
 * 손대지 않아도 후보로 뜬다(하드코딩 목록은 반드시 낡는다).
 * 순서: 구현 등재 순 → 계약에만 있는 키.
 */
export function registeredRowWidgetKeys(): string[] {
  const keys = Object.keys(COMPOSED_ROW_WIDGETS);
  for (const k of Object.keys(REGISTERED_OBJECT_FIELD_CONTRACTS)) {
    if (!keys.includes(k)) keys.push(k);
  }
  return keys;
}

/** 표시 라벨 — 라벨 등재가 없으면 키를 그대로 쓴다(등재 누락이 위젯을 감추지 않게). */
export function registeredRowWidgetLabel(key: string): string {
  return REGISTERED_ROW_WIDGET_LABELS[key] ?? key;
}

/** 위 키 목록 + 표시 라벨. admin '위젯 키' select 옵션의 정본. */
export function registeredRowWidgetOptions(): { key: string; label: string }[] {
  return registeredRowWidgetKeys().map((key) => ({ key, label: registeredRowWidgetLabel(key) }));
}
